/* eslint-disable jsdoc/check-param-names */
import { TextField, SxProps, Theme } from "@mui/material";
import { Dispatch, SetStateAction, useRef } from "react";

type Props = {
  id: string;
  type: string;
  label: string;
  text: string;
  setText: Dispatch<SetStateAction<any>>;
  maxLength: number;
  variant?: "outlined" | "filled" | "standard";
  onChange?: Function;
  size?: "small" | "medium";
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  isNumericText?: boolean;
  allowHalfKanaOnly?: boolean; // 半角ｶﾅのみ入力許可
  sx?: SxProps<Theme>;
  error?: boolean;
  helperText?: string;
};

/**
 * CustomTextField
 * @param id FieldID
 * @param type タイプ
 * @param label ラベル
 * @param text テキスト
 * @param setText セットテキスト
 * @param maxLength 最大文字
 * @param variant テキスト種類
 * @param size input大きさ
 * @param width 幅
 * @param fullWidth 幅いっぱいにするかどうか
 * @param required 必須かどうか
 * @param disabled 非活性
 * @param isNumericText 半角数字のみのテキスト
 * @param error エラーの場合true
 * @returns JSX.Element
 */
function CustomTextField({
  id,
  type,
  label,
  text,
  setText,
  maxLength,
  variant = "outlined",
  onChange = () => {},
  size = "medium",
  fullWidth,
  required,
  disabled,
  isNumericText,
  allowHalfKanaOnly,
  sx,
  error = false,
  helperText = "",
}: Props) {
  // ↓↓↓ 半角ｶﾅのみ許可コード
  const composing = useRef(false);
  const sanitizeHalfKana = (s: string) => (
    // 半角ASCII + 半角カナのみ残す
    s.replace(/[^\x20-\x7E\uFF61-\uFF9F]/g, "")
  );

  const handleChangeHalf = (value: string) => {
    if (composing.current) {
      // 変換中は触らない（IME対策）
      setText(value);
    } else {
      setText(sanitizeHalfKana(value));
    }
    onChange();
  };
  // ↑↑↑ 半角ｶﾅのみ許可コード

  const onChangeText = (value: string) => {
    // ｶﾅのみであればｶﾅを選別
    if (allowHalfKanaOnly) {
      handleChangeHalf(value);
      return;
    }
    // 数値テキストで数値以外の文字が入力されたら無視
    if (isNumericText && !/^\d*$/.test(value)) {
      return;
    }
    setText(value);
    onChange();
  };

  return (
    <TextField
      id={id}
      type={type}
      value={text}
      label={label}
      inputProps={{
        maxLength,
        pattern: allowHalfKanaOnly ? "[\\x20-\\x7E\\uFF61-\\uFF9F]*" : {},
      }}
      onCompositionStart={() => {if (allowHalfKanaOnly) composing.current = true}}
      onCompositionEnd={(e) => {
        if (allowHalfKanaOnly) {
          composing.current = false;
          const val = (e.target as HTMLInputElement).value
          const sanitizedVal = sanitizeHalfKana(val);
          if (sanitizedVal !== val) setText(sanitizedVal);
        }
      }}
      variant={variant}
      onChange={(e) => onChangeText(e.target.value)}
      size={size}
      fullWidth={fullWidth}
      required={required}
      disabled={disabled}
      error={error}
      helperText={helperText}
      sx={{
        ...sx,
        "& .MuiInputBase-root.Mui-disabled": {
          backgroundColor: (theme: Theme) => theme.palette.grey[300],
        },
      }}
    />
  );
}

export default CustomTextField;
